// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import { InvoiceLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CartLineContainer, {
    CartLineContainerStyles,
    MobileCartLineContainerStyles,
} from "@keller/App/Components/Cart/CartContainerLayout";
import cartLineSharedStyles from "@keller/App/SharedStyle/Cart/CartLineSharedStyles";
import LazyImage, { LazyImageProps } from "@keller/MobiusOverrides/LazyImage";
import ProductPartNumbers from "@keller/Overrides/Components/ProductPartNumbers";
import { mergeWithCss } from "@keller/XDL/Utilities";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    invoiceLine: InvoiceLineModel;
    extendedStyles?: InvoiceDetailsLineCardStyles;
    // XNG-Changes
    className?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    wishListSettings: getSettingsCollection(state).wishListSettings,
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface InvoiceDetailsLineCardStyles {
    container?: GridContainerProps;
    imageItem?: GridItemProps;
    imageLink?: LinkPresentationProps;
    image?: LazyImageProps;
    infoItem?: GridItemProps;
    infoInnerContainer?: GridContainerProps;
    infoLeftColumn?: GridItemProps;
    infoLeftColumnContainer?: GridContainerProps;
    brandItem?: GridItemProps;
    brandStyles?: ProductBrandStyles;
    productNameItem?: GridItemProps;
    productNameLink?: LinkPresentationProps;
    numbersItem?: GridItemProps;
    numbersInnerContainer?: GridContainerProps;
    partItem?: GridItemProps;
    partStyles?: SmallHeadingAndTextStyles;
    myPartItem?: GridItemProps;
    myPartStyles?: SmallHeadingAndTextStyles;
    mgfItem?: GridItemProps;
    mgfGridContainer?: GridContainerProps;
    mgfStyles?: SmallHeadingAndTextStyles;
    notesItem?: GridItemProps;
    notesStyles?: SmallHeadingAndTextStyles;
    infoRightColumn?: GridItemProps;
    infoRightColumnContainer?: GridContainerProps;
    priceItem?: GridItemProps;
    priceStyles?: SmallHeadingAndTextStyles;
    qtyItem?: GridItemProps;
    qtyStyles?: SmallHeadingAndTextStyles;
    totalItem?: GridItemProps;
    totalStyles?: SmallHeadingAndTextStyles;
    subtotalWithoutVatHeadingAndText?: SmallHeadingAndTextStyles;
    vatLabelText?: TypographyPresentationProps;
    secondaryPriceText?: TypographyPresentationProps;
    secondaryVatLabelText?: TypographyPresentationProps;
    productDescriptionText?: TypographyPresentationProps;
    // XNG-Changes
    cartLineContainer?: CartLineContainerStyles;
    productBrand?: ProductBrandStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityTextField?: TextFieldPresentationProps;
    addToListLink?: LinkPresentationProps;
    cartLineContainerMobileStyles?: MobileCartLineContainerStyles;
    lineNoteWrapperOnPrint?: InjectableCss;
    notesStylesOnPrint?: SmallHeadingAndTextStyles;
}

// XNG-Changes - Custom Style
export const cardStyles: InvoiceDetailsLineCardStyles = {
    imageLink: {
        ...cartLineSharedStyles.productImage?.linkWrappingImage,
    },
    image: {
        css: css`
            ${cartLineSharedStyles.productImage?.img?.css}
        `,
    },
    productBrand: {
        ...cartLineSharedStyles.productBrand,
    },
    productNameLink: {
        ...cartLineSharedStyles.productDescription?.productDetailLink,
    },
    productDescriptionText: {
        ...cartLineSharedStyles.productDescription?.productDetailDescription,
    },
    productPartNumbers: {
        ...cartLineSharedStyles.productPartNumbers,
    },
    qtyStyles: {
        ...cartLineSharedStyles.quantityHeadingAndText,
    },
    priceStyles: {
        ...cartLineSharedStyles.priceHeadingAndText,
    },
    totalStyles: {
        ...cartLineSharedStyles.extendedUnitNetPriceText,
    },
    subtotalWithoutVatHeadingAndText: {
        ...cartLineSharedStyles.extendedUnitNetPriceText,
    },
    notesStyles: {
        ...cartLineSharedStyles.readOnlyNotes,
    },
    quantityTextField: {
        ...mergeWithCss(cartLineSharedStyles.quantityTextField, {
            labelProps: {
                css: css`
                    @media print {
                        width: 85px;
                    }
                `,
            },
        }),
    },
    addToListLink: {
        ...cartLineSharedStyles.addToListLink,
    },
};

const InvoiceDetailsLineCard = ({
    invoiceLine,
    enableVat,
    vatPriceDisplay,
    showPricing,
    wishListSettings,
    className,
    extendedStyles,
    setAddToListModalIsOpen,
    addToWishList,
}: Props) => {
    const [styles] = React.useState(() => mergeWithCss(cardStyles, extendedStyles));
    const isShareEntity = getIsShareEntity();
    const toasterContext = React.useContext(ToasterContext);

    // XNG-changes: added add to list click handler
    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return null;
        }

        const productInfo = {
            productId: invoiceLine.properties.productId,
            qtyOrdered: invoiceLine.qtyInvoiced,
            unitOfMeasure: invoiceLine.unitOfMeasure,
        };

        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return null;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    // XNG-Change: use CartLineContainer
    return (
        <CartLineContainer
            className={className}
            extendedStyles={styles.cartLineContainer}
            mobileExtendedStyles={styles.cartLineContainerMobileStyles}
            data-test-selector={`invoiceDetails_invoiceLine_${invoiceLine.id}`}
        >
            <CartLineContainer.Image>
                <Link href={invoiceLine.productUri} {...styles.imageLink}>
                    <LazyImage
                        src={invoiceLine.mediumImagePath}
                        {...styles.image}
                        disableLazyLoad={true}
                        isShareEntity={isShareEntity}
                    />
                </Link>
            </CartLineContainer.Image>
            <CartLineContainer.Info>
                {invoiceLine.brand && (
                    <ProductBrand
                        brand={invoiceLine.brand}
                        showLogo={false}
                        data-test-selector="invoiceLine_brand"
                        extendedStyles={styles.productBrand}
                    />
                )}
                {invoiceLine.productUri && (
                    <Link
                        {...styles.productNameLink}
                        href={invoiceLine.productUri}
                        data-test-selector="invoiceLine_product"
                    >
                        {invoiceLine.shortDescription || invoiceLine.description}
                    </Link>
                )}
                {!invoiceLine.productUri && (
                    <Typography as="p" data-test-selector="invoiceLine_product" {...styles.productDescriptionText}>
                        {invoiceLine.shortDescription || invoiceLine.description}
                    </Typography>
                )}
                <ProductPartNumbers
                    productNumber={invoiceLine.productERPNumber}
                    customerProductNumber={invoiceLine.customerProductNumber}
                    manufacturerItem={invoiceLine.manufacturerItem}
                    extendedStyles={styles.productPartNumbers}
                />
                {invoiceLine.notes && (
                    <SmallHeadingAndText
                        {...styles.notesStyles}
                        heading={translate("Line Notes")}
                        text={invoiceLine.notes}
                    />
                )}
            </CartLineContainer.Info>
            <CartLineContainer.Qty>
                <TextField
                    value={invoiceLine.qtyInvoiced}
                    label={translate("QTY Invoiced")}
                    disabled={true}
                    {...styles.quantityTextField}
                />
            </CartLineContainer.Qty>
            <CartLineContainer.Price>
                {showPricing && (
                    <>
                        <SmallHeadingAndText
                            {...styles.priceStyles}
                            heading={translate("Price")}
                            text={
                                (enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? invoiceLine.unitPriceWithVatDisplay
                                    : invoiceLine.unitPriceDisplay) +
                                (invoiceLine.unitOfMeasure ? ` / ${invoiceLine.unitOfMeasure}` : "")
                            }
                        />
                        {enableVat && (
                            <>
                                <Typography as="p" {...styles.vatLabelText}>
                                    {vatPriceDisplay === "DisplayWithVat" ||
                                    vatPriceDisplay === "DisplayWithAndWithoutVat"
                                        ? `${translate("Inc. VAT")} (${invoiceLine.taxRate}%)`
                                        : translate("Ex. VAT")}
                                </Typography>
                                {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                    <>
                                        <Typography {...styles.secondaryPriceText} as="p">
                                            {invoiceLine.unitPriceDisplay +
                                                (invoiceLine.unitOfMeasure ? ` / ${invoiceLine.unitOfMeasure}` : "")}
                                        </Typography>
                                        <Typography as="p" {...styles.secondaryVatLabelText}>
                                            {translate("Ex. VAT")}
                                        </Typography>
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}
            </CartLineContainer.Price>
            <CartLineContainer.Total>
                {showPricing && (
                    <>
                        {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                            <SmallHeadingAndText
                                {...styles.subtotalWithoutVatHeadingAndText}
                                heading={`${translate("Total")} (${translate("Ex. VAT")})`}
                                text={invoiceLine.lineTotalDisplay}
                            />
                        )}
                        <SmallHeadingAndText
                            {...styles.totalStyles}
                            heading={
                                !enableVat
                                    ? translate("Total")
                                    : vatPriceDisplay !== "DisplayWithoutVat"
                                      ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                      : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                            }
                            text={
                                enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? invoiceLine.netPriceWithVatDisplay
                                    : invoiceLine.lineTotalDisplay
                            }
                        />
                    </>
                )}
            </CartLineContainer.Total>
            {/* XNG-changes: added add to list link  */}
            <CartLineContainer.PeripheralActionsLeft>
                <StyledWrapper>
                    {invoiceLine.properties?.canAddToWishlist?.toLowerCase() === "true" && (
                        <Link onClick={addToListClickHandler} {...styles.addToListLink}>
                            {translate("Add to List")}
                        </Link>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.lineNoteWrapperOnPrint}>
                    {true && (
                        <SmallHeadingAndText
                            {...styles.notesStylesOnPrint}
                            heading={`${translate("Line Notes")}:`}
                            text={invoiceLine.lineTotalDisplay}
                        />
                    )}
                </StyledWrapper>
            </CartLineContainer.PeripheralActionsLeft>
        </CartLineContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceDetailsLineCard);
